import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CloudOff, Loader2 } from "lucide-react";
import * as queue from "@/lib/offline-queue";

/**
 * Header status chip.
 *
 * Shows nothing when everything is fine (online + empty queue).
 * When offline OR when there are pending queued clicks, it renders a
 * subtle warm chip: "Offline · N pending" or "Syncing · N left".
 *
 * The chip updates on `online` / `offline` events and on the
 * pub/sub notifications emitted by `offline-queue`.
 */
export default function OfflineChip() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const refresh = async () => setPending(await queue.count());
    refresh();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsub = queue.subscribe(refresh);
    // Poll every 3s as a belt+braces safety net (in case notify() is missed).
    const id = window.setInterval(refresh, 3000);
    // Watch a global event our Dashboard fires while draining
    const onSync = (e) => setSyncing(!!e.detail);
    window.addEventListener("refclick:sync", onSync);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("refclick:sync", onSync);
      window.clearInterval(id);
      unsub && unsub();
    };
  }, []);

  const visible = !online || pending > 0;
  const label = !online
    ? `Offline · ${pending} pending`
    : syncing
      ? `Syncing · ${pending} left`
      : `Queued · ${pending}`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="offline-chip"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
          data-testid="offline-chip"
          data-online={online ? "true" : "false"}
          data-pending={pending}
          className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 bg-yellow-400/10 px-2.5 py-1 font-mono-safe text-[11px] font-semibold text-yellow-200"
          style={{ boxShadow: "0 0 10px rgba(250, 204, 21, 0.25)" }}
        >
          {syncing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CloudOff className="h-3 w-3" />
          )}
          <span>{label}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
